import React from 'react';
import SPAButton from '../shared/SPAButton';
import FilterPill from './FilterPill';
import FilterSelect from './FilterSelect';
import type { OptionKind } from './useFilterOptions';
import { STATE_LABELS } from './syncStates';
import type { FilterKey, SyncFilters as Filters } from './useSyncRows';

// AIDEV-NOTE: Input ids are namespaced because the legacy admin stylesheet carries bare id
// selectors — `#subscriber_id { visibility: hidden }` in _admin.scss hid this filter completely, and
// nothing in the component or its classes hinted at why. Never give an SPA control a bare model-ish id.
const fieldId = (key: string) => `grade-sync-filter-${key}`;

const FIELDS: { key: FilterKey; kind: OptionKind; label: string }[] = [
  { key: 'staffer_id', kind: 'staffer', label: 'Teacher' },
  { key: 'subscriber_id', kind: 'subscriber', label: 'District / subscriber' },
  { key: 'classroom_id', kind: 'classroom', label: 'Section / class' },
  { key: 'assignment_id', kind: 'assignment', label: 'Assignment' },
  { key: 'student_id', kind: 'student', label: 'Student' },
];

const STATES = ['not_delivered', 'out_of_sync', 'in_sync'] as const;

interface SyncFiltersProps {
  draft: Filters;
  grain: 'assignments' | 'scores';
  /** Null when nothing is listed here — the teacher page lists it instead. */
  matched: number | null;
  onClear: () => void;
  /** Picking an option applies it straight away; there is nothing left to type and submit. */
  onPick: (key: FilterKey, value: string) => void;
  onPlatformChange: (value: string) => void;
  onStateChange: (value: string) => void;
  platform: string;
  platforms: string[];
  state: string;
  total: number | null;
}

const SyncFilters = ({
  draft,
  grain,
  matched,
  onClear,
  onPick,
  onPlatformChange,
  onStateChange,
  platform,
  platforms,
  state,
  total,
}: SyncFiltersProps) => (
  <div className="tw-mb-5">
    <p className="tw-mb-3 tw-mt-0 tw-max-w-3xl tw-text-sm tw-text-spa-secondary">
      Filters combine — an assignment plus a student narrows to one gradebook cell. Pick a teacher first
      to choose their classes, assignments and students by name.
    </p>

    <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-5">
      {FIELDS.map(field => (
        <div key={field.key}>
          <label
            className="tw-mb-1 tw-block tw-text-xs tw-text-spa-secondary"
            htmlFor={fieldId(field.key)}
          >
            {field.label}
          </label>
          <FilterSelect
            inputId={fieldId(field.key)}
            kind={field.kind}
            scope={{ staffer_id: draft.staffer_id || undefined, subscriber_id: draft.subscriber_id || undefined }}
            value={draft[field.key] ?? ''}
            onChange={value => onPick(field.key, value)}
          />
        </div>
      ))}
    </div>

    <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      {/* Score states only describe score rows — they are dead controls against the assignment grain. */}
      {grain === 'scores' && (
        <>
          <FilterPill active={state === ''} onClick={() => onStateChange('')}>All statuses</FilterPill>
          {STATES.map(value => (
            <FilterPill key={value} active={state === value} tone={value} onClick={() => onStateChange(value)}>
              {STATE_LABELS[value]}
            </FilterPill>
          ))}

          <span aria-hidden="true" className="tw-mx-2 tw-h-5 tw-w-px tw-bg-spa-border" />
        </>
      )}

      <FilterPill active={platform === ''} onClick={() => onPlatformChange('')}>All platforms</FilterPill>
      {platforms.map(value => (
        <FilterPill key={value} active={platform === value} onClick={() => onPlatformChange(value)}>
          {value}
        </FilterPill>
      ))}
    </div>

    <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-spa-secondary">
      {total != null && (
        <span>{`${matched} of ${total} ${grain === 'assignments' ? 'assignments' : 'sync records'}`}</span>
      )}
      <SPAButton size="xs" onClick={onClear}>Clear all filters</SPAButton>
    </div>
  </div>
);

export default SyncFilters;
